import {
  createApi, createHttpClient, createIdempotencyStore, isRetryable, v3,
  type Api, type IdempotencyStore,
} from '@greensm/api-client';
import { createAuthStore, type AuthStore } from '@greensm/auth';
import { QueryClient } from '@tanstack/react-query';
import { createContext, useContext, type ReactNode } from 'react';
import { resetAll } from '@/shared/lib/resetRegistry';
import { clearSessionToken, devUser, v3Token, writeDevUser, writeSessionToken } from './config/devAccount';

export interface Services {
  api: Api;
  auth: AuthStore;
  queryClient: QueryClient;
  idem: IdempotencyStore;
  /** URL luồng SSE (NT-03). */
  eventsUrl: string;
  getAccessToken: () => string | null;
  /** fetch dùng chung (giả lập khi mock) – SSE cũng dùng cùng nguồn. */
  fetchImpl?: typeof fetch;
  /** API v3 (hợp đồng 04/10) – màn mới dùng cái này; màn cũ vẫn dùng `api`. */
  v3: v3.V3Api;
  /** URL luồng SSE v3 `/api/v3/stream`. */
  v3StreamUrl: string;
  /** Token phiên (đăng nhập mật khẩu) hoặc token dev `dev-<username>` (00 §3) cho REST và SSE v3. */
  getV3Token: () => string;
  /** fetch cho v3 (giả lập khi mock); undefined = fetch thật. */
  v3FetchImpl?: typeof fetch;
  /** false khi chạy mock v1 mà không có backend v3 giả (vd. test màn cũ) ⇒ không gọi /api/v3. */
  v3Enabled: boolean;
}

export interface ServicesOptions {
  baseUrl: string;
  fetchImpl?: typeof fetch;
  idempotencyStorage?: Storage | null;
  apiMode?: 'mock' | 'live';
  devUsername?: string;
  /** Gốc API v3, mặc định `/api/v3` (Vite proxy sang Backend :18000). */
  v3BaseUrl?: string;
  /** Backend v3 giả lập (mock/test). Không truyền ⇒ gọi thật. */
  v3FetchImpl?: typeof fetch;
}

const UID_KEY = 'gsm.uid';
const readUid = (): string | null => { try { return sessionStorage.getItem(UID_KEY); } catch { return null; } };
const writeUid = (id: string | null) => { try { if (id) sessionStorage.setItem(UID_KEY, id); else sessionStorage.removeItem(UID_KEY); } catch { /* noop */ } };

/** Lắp ráp toàn bộ dịch vụ dùng chung. Test tạo bản riêng với fetch giả. */
export function createServices(opts: ServicesOptions): Services {
  const idem = createIdempotencyStore(opts.idempotencyStorage);
  const live = (opts.apiMode ?? (opts.fetchImpl ? 'mock' : 'live')) === 'live';

  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 15_000,
        refetchOnWindowFocus: true,
        // Chỉ tự thử lại lỗi tạm thời (mạng / 5xx); lỗi nghiệp vụ, 401, 403 thì hiện ngay.
        retry: (count, err) => count < 2 && isRetryable(err),
        retryDelay: (n) => Math.min(1000 * 2 ** n, 8000),
      },
      mutations: { retry: false },
    },
  });

  // eslint-disable-next-line prefer-const
  let auth: AuthStore;
  const http = createHttpClient({
    baseUrl: opts.baseUrl,
    fetchImpl: opts.fetchImpl,
    getAccessToken: () => auth.getAccessToken(),
    refreshSession: () => auth.refresh(),
    onSessionExpired: () => auth.markExpired(),
    responseEnvelope: live ? 'v2' : 'raw',
    cookieSession: live,
  });
  // Tài khoản dev đọc lúc gọi: màn đăng nhập cho chọn cus1 / cus2 (05/10).
  const liveOptions = live ? {
    mode: 'live' as const,
    get devUsername() { return devUser(opts.devUsername); },
    onSessionToken: (token: string | null, username?: string) => {
      if (token) writeSessionToken(token); else clearSessionToken();
      if (username) writeDevUser(username);
    },
  } : undefined;
  const api = createApi(http, liveOptions);

  // API v3: chuyển mock ↔ live chỉ bằng cấu hình (VITE_API_MODE), màn hình không đổi code.
  const v3BaseUrl = (opts.v3BaseUrl ?? '/api/v3').replace(/\/$/, '');
  const getV3Token = () => v3Token(devUser(opts.devUsername));
  const v3Api = v3.createV3Api(createHttpClient({
    baseUrl: v3BaseUrl,
    fetchImpl: opts.v3FetchImpl,
    responseEnvelope: 'v3',
    getAccessToken: getV3Token,
    // Token dev không làm mới được; 401 ⇒ báo lỗi để người dùng đăng nhập lại.
    refreshSession: async () => false,
    onSessionExpired: () => {},
  }));

  auth = createAuthStore({
    cookieSession: live,
    gateway: {
      login: api.auth.login,
      refresh: api.auth.refresh,
      me: () => api.auth.me(),
      logout: api.auth.logout,
    },
    // Đổi người dùng / đăng xuất / hết phiên ⇒ dọn sạch dữ liệu cũ.
    // Khôi phục CÙNG người dùng sau khi tải lại trang thì KHÔNG dọn (giữ yêu cầu đặt chuyến đang chờ).
    onUserChange: (prev, next) => {
      const lastUid = readUid();
      const switched = prev !== null || next === null || (lastUid !== null && lastUid !== next.id);
      if (switched) {
        queryClient.clear();
        idem.clearAll();
        resetAll();
      }
      writeUid(next?.id ?? null);
    },
  });

  return {
    api, auth, queryClient, idem,
    eventsUrl: live ? opts.baseUrl.replace(/\/api(?:\/v[23])?\/?$/, '').replace(/\/$/, '') + '/api/v2/events' : opts.baseUrl.replace(/\/$/, '') + '/v1/events',
    getAccessToken: () => auth.getAccessToken(),
    fetchImpl: opts.fetchImpl,
    v3: v3Api,
    v3StreamUrl: v3BaseUrl + v3.v3Paths.stream,
    getV3Token,
    v3FetchImpl: opts.v3FetchImpl,
    v3Enabled: !opts.fetchImpl || !!opts.v3FetchImpl,
  };
}

const Ctx = createContext<Services | null>(null);

export function ServicesProvider({ services, children }: { services: Services; children: ReactNode }) {
  return <Ctx.Provider value={services}>{children}</Ctx.Provider>;
}

export function useServices(): Services {
  const s = useContext(Ctx);
  if (!s) throw new Error('useServices phải nằm trong <ServicesProvider>');
  return s;
}

export const useApi = (): Api => useServices().api;
